import React from 'react';
import { Linkedin, Instagram, Youtube, Star, Truck, Shield } from 'lucide-react';
import cityPages from '../data/cityPages.json';

const Footer = () => {
  const handleLinkClick = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  const footerLinks = [
    {
      title: 'Products',
      links: [
        { label: 'SMARTBOT — Floor Cleaning Robots', href: '/floor-cleaning-robots' },
        { label: 'POOLBOT — Pool Cleaning Robots', href: '/pool-cleaning-robots' },
        { label: 'SHINEBOT — Window Cleaning Robots', href: '/window-cleaning-robots' },
        { label: 'LAWNBOT — Robotic Lawn Mowers', href: '/robotic-lawn-mowers' },
      ],
    },
    {
      title: 'Quick Links',
      links: [
        { label: 'About Us', href: '/about-us' },
        { label: 'Why Harpalyk', id: 'why-harpalyk' },
        { label: 'Dealer Registration', href: '/dealer' },
        { label: 'Blog', href: '/blog' },
        { label: 'Book a Demo', id: 'contact' },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Contact Us', id: 'contact' },
        { label: 'Privacy Policy', href: '/privacy-policy' },
        { label: 'Shipping Policy', href: '/shipping-policy' },
        { label: 'Warranty Policy', href: '/warranty-policy' },
        { label: 'Return & Refund Policy', href: '/return-refund-policy' },
        { label: 'Terms of Service', href: '/terms-of-service' },
      ],
    },
  ];

  const socialLinks = [
    { icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com/in/harpalyk-engineering-203975379' },
    { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/harpalyk_engineering?igsh=MTFhaXdqcWh0ZW9leA==' },
    { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/@harpalyk-Robos' },
  ];

  return (
    <footer className="bg-gray-900 text-white px-6 lg:px-12 py-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/qak1yr3g_PHOTO-2025-11-08-13-42-05.jpg" 
                alt="Harpalyk Logo" 
                className="h-10 w-auto object-contain"
              />
              <h3 className="text-3xl font-light">Harpalyk</h3>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Intelligent home solutions that enhance freedom, creativity, and quality of life for millions of families worldwide.
            </p>
            <div className="space-y-2 mb-6">
              <p className="text-gray-400 text-sm">
                <strong className="text-gray-300">Email:</strong> sales@harpalyk.com, support@harpalyk.com
              </p>
              <p className="text-gray-400 text-sm">
                <strong className="text-gray-300">Phone:</strong> +91-7011370322
              </p>
              <p className="text-gray-400 text-sm">
                <strong className="text-gray-300">Location:</strong> Gurugram, India
              </p>
            </div>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links */}
          {footerLinks.map((section, index) => (
            <div key={index}>
              <h4 className="text-lg font-light mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.href ? (
                      <a
                        href={link.href}
                        className="text-gray-400 hover:text-white transition-colors duration-200 text-sm text-left"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <button
                        onClick={() => handleLinkClick(link.id)}
                        className="text-gray-400 hover:text-white transition-colors duration-200 text-sm text-left"
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

      {/* SEO internal links: cities & industries */}
        <div className="pt-8 border-t border-gray-800 mb-8">
          <div className="grid md:grid-cols-2 gap-8 mb-2">
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-3">Cities We Serve</h4>
              <p className="text-xs leading-6 text-gray-500">
                {cityPages.map((c) => [c.slug, c.city]).map(([slug, name], i, arr) => (
                  <span key={slug}>
                    <a href={`/cleaning-robots-in-${slug}`} className="hover:text-white transition-colors duration-200">{name}</a>
                    {i < arr.length - 1 ? ' · ' : ''}
                  </span>
                ))}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-3">Industries</h4>
              <p className="text-xs leading-6 text-gray-500">
                {[['hotels','Hotels'],['hospitals','Hospitals'],['schools','Schools'],['factories','Factories'],['shopping-malls','Malls'],['luxury-villas','Luxury Villas'],['apartments','Apartments'],['corporate-offices','Corporate Offices'],['airports','Airports'],['railways','Railways'],['warehouses','Warehouses'],['swimming-pools','Swimming Pools'],['resorts','Resorts'],['facility-management','Facility Management']].map(([slug, name], i, arr) => (
                  <span key={slug}>
                    <a href={`/solutions/${slug}`} className="hover:text-white transition-colors duration-200">{name}</a>
                    {i < arr.length - 1 ? ' · ' : ''}
                  </span>
                ))}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg">
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
              <span className="text-gray-300 text-sm font-medium">4.8/5 Rating (500+ Reviews)</span>
            </div>
            <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg">
              <Truck className="w-5 h-5 text-emerald-500" />
              <span className="text-gray-300 text-sm font-medium">Free Delivery Pan-India</span>
            </div>
            <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg">
              <Shield className="w-5 h-5 text-emerald-500" />
              <span className="text-gray-300 text-sm font-medium">2-Year Warranty</span>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2026 Harpalyk Engineering Private Limited. All rights reserved.
            </p>
            <p className="text-gray-500 text-sm">
              Founded in 2024 · Building the Future of Home Robotics
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;